import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { EMBLEM_OUTER } from "@/lib/emblemPaths";

const EASE = [0.16, 1, 0.3, 1];

const Ring = ({ text }) => (
  <svg viewBox="0 0 300 300" className="absolute inset-0 w-full h-full spin-slow" aria-hidden="true">
    <defs>
      <path id="ring-path" d="M150,150 m-128,0 a128,128 0 1,1 256,0 a128,128 0 1,1 -256,0" />
    </defs>
    <text className="font-display" fill="var(--gold)" fontSize="15" letterSpacing="5">
      <textPath href="#ring-path" textLength="800" lengthAdjust="spacing">{text.repeat(2)}</textPath>
    </text>
  </svg>
);

export const WarrantyBadge = ({ ring, unit }) => {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(useTransform(my, [-0.5, 0.5], [14, -14]), { stiffness: 120, damping: 14 });
  const ry = useSpring(useTransform(mx, [-0.5, 0.5], [-14, 14]), { stiffness: 120, damping: 14 });
  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };
  const reset = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <div onMouseMove={onMove} onMouseLeave={reset} className="relative w-[min(84vw,440px)] aspect-square mx-auto [perspective:1000px]" data-testid="warranty-badge">
      <div className="absolute inset-[14%] rounded-full bg-[var(--gold)]/25 blur-[70px] badge-glow" aria-hidden="true" />
      <Ring text={ring} />
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="absolute inset-[12%] [container-type:inline-size]" data-testid="warranty-medallion">
        <svg viewBox="40 45 664 652" className="absolute inset-0 w-full h-full" aria-hidden="true">
          <defs>
            <linearGradient id="wb-gold" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#FFE3A3" />
              <stop offset="45%" stopColor="#E5A93C" />
              <stop offset="100%" stopColor="#B9811E" />
            </linearGradient>
            <radialGradient id="wb-disk" cx="0.38" cy="0.32" r="0.75">
              <stop offset="0%" stopColor="#26304a" />
              <stop offset="100%" stopColor="#0A0D14" />
            </radialGradient>
          </defs>
          <circle cx="371.7" cy="370.7" r="300" fill="#0A0D14" fillOpacity="0.55" />
          <path d={EMBLEM_OUTER} fill="url(#wb-gold)" fillRule="evenodd" className="emblem-spin" />
          <circle cx="371.7" cy="370.7" r="156" fill="url(#wb-disk)" stroke="url(#wb-gold)" strokeWidth="6" />
          <circle cx="371.7" cy="370.7" r="138" fill="none" stroke="url(#wb-gold)" strokeWidth="3" />
          {[[0, -147], [147, 0], [0, 147], [-147, 0]].map(([dx, dy], i) => (
            <circle key={i} cx={371.7 + dx} cy={370.7 + dy} r="7" fill="url(#wb-gold)" />
          ))}
        </svg>
        <div className="absolute inset-[27%] rounded-full overflow-hidden" aria-hidden="true">
          <div className="absolute inset-0 badge-shine" />
        </div>
        <div className="absolute inset-0 grid place-items-center">
          <motion.div
            initial={{ scale: 0.4, opacity: 0, rotateY: -90 }}
            whileInView={{ scale: 1, opacity: 1, rotateY: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 1.4, ease: EASE, delay: 0.2 }}
            className="text-center"
            style={{ transform: "translateZ(50px)" }}
          >
            <div className="font-display font-bold text-[26cqw] leading-[0.9] gold-text">3</div>
            <div className="font-display text-[3.6cqw] tracking-[0.35em] uppercase text-[var(--text)] mt-[1cqw] pl-[0.35em]">{unit}</div>
          </motion.div>
        </div>
        {[0, 1, 2].map((i) => (
          <span key={i} className="sparkle" style={{ top: ["6%", "72%", "18%"][i], left: ["80%", "90%", "6%"][i], animationDelay: `${i * 0.9}s` }} aria-hidden="true" />
        ))}
      </motion.div>
    </div>
  );
};
